import React, { useState, useEffect, useMemo, useCallback } from "react";
import Pagination from "../../components/Pagination";
import CashierApi from "../../utility/CashierApi";
import { useQuery } from "@tanstack/react-query";
import loader from "../../assets/loader.webp";
import { useNavigate } from "react-router";
import ProductCard from "./ProductCard";

function AllProducts({ searched }) {
  const { fetchData } = CashierApi();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["products", { searched, currentPage }],
    keepPreviousData: true,
    queryFn: () => fetchData("products", currentPage, searched),
  });

  // Memoize items
  const items = useMemo(() => {
    return Array.isArray(data?.data) ? data?.data : [];
  }, [data]);

  // Update total pages when the data changes
  useEffect(() => {
    if (data) {
      setTotalPage(data?.last_page);
    }
  }, [data]);

  // Reset current page when searched changes
  useEffect(() => {
    if (searched) {
      setCurrentPage(1);
    }
  }, [searched]);

  // Memoize handlePageChange
  const handlePageChange = useCallback(
    (page) => {
      if (page === currentPage) return;

      if (page > currentPage) {
        setIsNextLoading(true);
        setIsPrevLoading(false);
      } else {
        setIsPrevLoading(true);
        setIsNextLoading(false);
      }

      setCurrentPage(page);
      setIsNextLoading(false);
      setIsPrevLoading(false);
    },
    [currentPage]
  );

  // Memoize loading spinner
  const loadingSpinner = useMemo(() => {
    return (
      <div className="h-full w-full flex justify-center items-center">
        <img src={loader} alt="loading..." className="w-12" />
      </div>
    );
  }, []);

  // Memoize product grid
  const productGrid = useMemo(() => {
    // h-[85%] gap-4 px-12
    return (
      <div className="grid grid-cols-4 grid-rows-2 w-full gap-4 px-4 ">
        {items?.map((item) => (
          <ProductCard key={item?.id} item={item} />
        ))}
      </div>
    );
  }, [items]);

  if (isLoading && currentPage === 1) {
    return loadingSpinner;
  }

  // if (error) {
  //   return <div>Error fetching products</div>;
  // }

  if (data?.status === 401 || data?.response?.status === 401) {
    return navigate("/login");
  }

  return (
    <div className="h-[calc(100vh-150px)] pb-20 px-4 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
      {isLoading ? loadingSpinner : productGrid}

      {/* Pagination component */}
      <Pagination
        onPageChange={handlePageChange}
        totalPage={totalPage}
        currentPage={currentPage}
        setIsNextLoading={setIsNextLoading}
        setIsPrevLoading={setIsPrevLoading}
        isPrevLoading={isPrevLoading}
        isNextLoading={isNextLoading}
      />
    </div>
  );
}

export default React.memo(AllProducts);
